import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { TreeItem, TreeView } from '@material-ui/lab';
import { Add, ChevronRight, ExpandMore } from '@material-ui/icons';
import ym from 'react-yandex-metrika';
import { USER_OPERATIONS } from 'helper';
import { useQuery } from 'hooks/useQuery';
import { RootState } from 'interfaces';
import { Confluence } from '../interfaces';
import { CreateArticle } from '../components';
import { findParentMenus } from '../helpers';

interface IProps {
  menu?: {
    loading: boolean;
    data: Confluence.IMenu;
    error: string | null;
  };
  onSelect(menuItem: Confluence.IMenuItem): void;
  onCreate(article: Confluence.IArticle): void;
}

const MenuRenderer = ({ menu, onSelect, onCreate }: IProps) => {
  const params = useQuery();

  const articleId = params.get('articleId')!;

  function handleMenuItemClick(event: React.MouseEvent<Element>, menuItem: Confluence.IMenuItem) {
    event.preventDefault();

    if (menuItem.menuId === +articleId) return;

    if (menuItem.templateType === Confluence.ETemplateTypes.LESSON)
      ym('reachGoal', '[click] Статья типа "Извлеченные уроки"', { templateType: menuItem.templateType });

    onSelect(menuItem);
  }

  return (
    <>
      {menu?.loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <CircularProgress />
        </Box>
      ) : Boolean(menu?.error) ? (
        <Box p={2}>
          <Typography component="h3" variant="h6" align="center">
            {menu?.error}
          </Typography>
        </Box>
      ) : (
        menu?.data && (
          <MenuTree
            menu={menu.data}
            selectedNodeId={articleId || ''}
            onSelect={handleMenuItemClick}
            onCreate={onCreate}
          />
        )
      )}
    </>
  );
};

const useStyles = makeStyles({
  label: { padding: '10px' },
});

interface IMenuTreeProps {
  menu: Confluence.IMenu;
  selectedNodeId?: string;
  onSelect(event: React.MouseEvent<Element, MouseEvent>, menuItem: Confluence.IMenuItem): void;
  onCreate?(article: Confluence.IArticle): void;
}

export const MenuTree = ({ menu, selectedNodeId, onSelect, onCreate }: IMenuTreeProps) => {
  const operations = useSelector((state: RootState) => state.operations);

  const classes = useStyles();

  const [expandedMenuNodes, setExpandedMenuNodes] = useState(() =>
    findParentMenus(menu || [], +selectedNodeId!).map(({ menuId }) => String(menuId)),
  );
  const [selectedMenuNode, setSelectedMenuNode] = useState(selectedNodeId || '');

  const handleToggleMenu = useCallback((_: React.ChangeEvent<{}>, nodeIds: string[]) => {
    setExpandedMenuNodes(nodeIds);
  }, []);

  const handleSelectMenuItem = useCallback((_: React.ChangeEvent<{}>, nodeId: string) => {
    setSelectedMenuNode(nodeId);
  }, []);

  // меню приходит асинхронно, поэтому раскрытые узлы пересчитываем и после его загрузки —
  // иначе при заходе по прямой ссылке дерево остаётся свёрнутым
  useEffect(() => {
    setSelectedMenuNode(selectedNodeId || '');

    if (!selectedNodeId) return;

    const parentNodeIds = findParentMenus(menu || [], +selectedNodeId).map(({ menuId }) => String(menuId));

    setExpandedMenuNodes((prevNodes) => Array.from(new Set([...prevNodes, ...parentNodeIds])));
  }, [menu, selectedNodeId]);

  const renderTree = (menuItem: Confluence.IMenuItem) => (
    <TreeItem
      key={menuItem.menuId}
      nodeId={String(menuItem.menuId)}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography>{menuItem.name}</Typography>

          {[USER_OPERATIONS.CONFLUENCE_MANAGER, USER_OPERATIONS.CONFLUENCE_LESSON_MANAGER].some((operation) =>
            operations.includes(operation),
          ) && (
            <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
              {menuItem.menuId === +selectedMenuNode && (
                <CreateArticle parentId={menuItem.menuId} onCreate={onCreate}>
                  {(onOpen) => (
                    <Tooltip title="Добавить статью">
                      <IconButton size="small" aria-label="Добавить статью" onClick={onOpen}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CreateArticle>
              )}
            </Box>
          )}
        </Box>
      }
      classes={classes}
      onLabelClick={(event) => onSelect(event, menuItem)}
    >
      {Array.isArray(menuItem.child) ? menuItem.child.map((menuItem) => renderTree(menuItem)) : null}
    </TreeItem>
  );

  return (
    <TreeView
      aria-label="Меню"
      selected={selectedMenuNode}
      expanded={expandedMenuNodes}
      defaultCollapseIcon={<ExpandMore />}
      defaultExpandIcon={<ChevronRight />}
      onNodeToggle={handleToggleMenu}
      onNodeSelect={handleSelectMenuItem}
    >
      {(menu || []).map(renderTree)}
    </TreeView>
  );
};

export default MenuRenderer;
